import { useQueryClient } from '@tanstack/react-query'
import { useTeam } from 'app/frontend/base/config/context/team'
import { UpdateEventActivityPayload } from 'app/frontend/base/api/event'

type OptimisticEventSnapshot = {
  event?: wowaudit.Event
  eventReferences?: wowaudit.EventReference[]
}

type UpdateSelectionVariables = {
  eventParticipantId: number
  role?: string
  specId?: number | null
  characterId?: number
  selected?: boolean
  activityId?: number
}

type UpdateActivitySelectionVariables = {
  activityId: number
  selected: boolean
}

type UpdateActivityVariables = {
  activityId: number
  eventActivity: UpdateEventActivityPayload
}

type UpdateActivityOrderVariables = {
  activityOrder: number[]
}

const useOptimisticEventUpdate = (eventId?: number) => {
  const queryClient = useQueryClient()
  const { team } = useTeam()

  const teamEventQueryKey = eventId
    ? (['teamEvent', { teamId: team.id, eventId }] as const)
    : null
  const teamEventsQueryKey = ['teamEvents', { id: team.id }] as const

  const snapshot = async (): Promise<OptimisticEventSnapshot> => {
    if (teamEventQueryKey) {
      await queryClient.cancelQueries({ queryKey: teamEventQueryKey })
    }
    await queryClient.cancelQueries({ queryKey: teamEventsQueryKey })

    return {
      event: teamEventQueryKey
        ? queryClient.getQueryData<wowaudit.Event>(teamEventQueryKey)
        : undefined,
      eventReferences:
        queryClient.getQueryData<wowaudit.EventReference[]>(teamEventsQueryKey),
    }
  }

  const applyPatchedEvent = (event: wowaudit.Event) => {
    if (teamEventQueryKey) {
      queryClient.setQueryData(teamEventQueryKey, event)
    }
    queryClient.setQueryData(
      teamEventsQueryKey,
      (previous: wowaudit.EventReference[] | undefined) => {
        if (!previous) return previous
        return previous.map((eventReference) =>
          eventReference.id === event.id
            ? { ...eventReference, ...event }
            : eventReference,
        )
      },
    )
  }

  const rollback = (previous: OptimisticEventSnapshot | undefined) => {
    if (!previous) return
    if (teamEventQueryKey && previous.event) {
      queryClient.setQueryData(teamEventQueryKey, previous.event)
    }
    if (previous.eventReferences) {
      queryClient.setQueryData(teamEventsQueryKey, previous.eventReferences)
    }
  }

  const optimisticStatusUpdate = async ({
    eventParticipantId,
    status,
  }: {
    eventParticipantId: number
    status: wowaudit.EventParticipantReference['status']
  }) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const event = previous.event
    const nextEvent: wowaudit.Event = {
      ...event,
      participants: event.participants.map((participant) =>
        participant.id === eventParticipantId
          ? { ...participant, status }
          : participant,
      ),
      participantReferences: event.participantReferences.map(
        (participantReference) =>
          participantReference.id === eventParticipantId
            ? { ...participantReference, status }
            : participantReference,
      ),
    }
    applyPatchedEvent(nextEvent)
    return previous
  }

  const optimisticSelectionUpdate = async ({
    eventParticipantId,
    role,
    specId,
    characterId,
    selected,
    activityId,
  }: UpdateSelectionVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const event = previous.event
    const nextEvent: wowaudit.Event = {
      ...event,
      participants: event.participants.map((participant) => {
        if (participant.id !== eventParticipantId) return participant

        const nextParticipant: wowaudit.EventParticipant = { ...participant }
        if (activityId === undefined) {
          if (role !== undefined) {
            nextParticipant.role = role as wowaudit.EventParticipant['role']
          }
          if (specId !== undefined) {
            if (specId === null) {
              delete nextParticipant.specId
            } else {
              nextParticipant.specId = specId
            }
          }
          if (characterId !== undefined) {
            nextParticipant.altCharacterId = characterId
          }
        }

        const nextSelections = { ...nextParticipant.selections }
        const applySelection = (targetActivityId: number) => {
          const key = targetActivityId.toString()
          const currentSelection = nextSelections[key] ?? {}
          const updatedSelection = { ...currentSelection }
          if (selected !== undefined) updatedSelection.selected = selected
          if (role !== undefined) updatedSelection.role = role
          if (specId !== undefined) updatedSelection.spec_id = specId
          if (characterId !== undefined)
            updatedSelection.character_id = characterId
          nextSelections[key] = updatedSelection
        }

        if (activityId !== undefined) {
          applySelection(activityId)
        } else if (
          selected !== undefined ||
          role !== undefined ||
          specId !== undefined ||
          characterId !== undefined
        ) {
          event.activities.forEach((activity) => applySelection(activity.id))
        }

        nextParticipant.selections = nextSelections
        return nextParticipant
      }),
      participantReferences: event.participantReferences.map(
        (participantReference) => {
          if (participantReference.id !== eventParticipantId)
            return participantReference

          const nextParticipantReference: wowaudit.EventParticipantReference = {
            ...participantReference,
          }
          if (activityId === undefined) {
            if (role !== undefined) {
              nextParticipantReference.role =
                role as wowaudit.EventParticipantReference['role']
            }
            if (specId !== undefined) {
              if (specId === null) {
                delete nextParticipantReference.specId
              } else {
                nextParticipantReference.specId = specId
              }
            }
            if (characterId !== undefined) {
              nextParticipantReference.altCharacterId = characterId
            }
          }
          return nextParticipantReference
        },
      ),
    }

    applyPatchedEvent(nextEvent)
    return previous
  }

  const optimisticActivitySelectionUpdate = async ({
    activityId,
    selected,
  }: UpdateActivitySelectionVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const event = previous.event
    const nextEvent: wowaudit.Event = {
      ...event,
      participants: event.participants.map((participant) => {
        if (['absent', 'missing', 'removed'].includes(participant.status)) {
          return participant
        }

        return {
          ...participant,
          selections: {
            ...participant.selections,
            [activityId]: {
              ...(participant.selections[activityId] ?? {}),
              selected,
            },
          },
        }
      }),
    }
    applyPatchedEvent(nextEvent)
    return previous
  }

  const optimisticActivityUpdate = async ({
    activityId,
    eventActivity,
  }: UpdateActivityVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const event = previous.event
    const normalizedActivityUpdate: Partial<wowaudit.EventActivity> = {
      ...(eventActivity.active === undefined
        ? {}
        : { active: eventActivity.active }),
      ...(eventActivity.order === undefined
        ? {}
        : { order: eventActivity.order }),
      ...(typeof eventActivity.raidPlanId === 'number'
        ? { raidPlanId: eventActivity.raidPlanId }
        : {}),
    }
    const nextEvent: wowaudit.Event = {
      ...event,
      activities: event.activities.map((activity) =>
        activity.id === activityId
          ? { ...activity, ...normalizedActivityUpdate }
          : activity,
      ),
    }
    applyPatchedEvent(nextEvent)
    return previous
  }

  const optimisticActivityOrderUpdate = async ({
    activityOrder,
  }: UpdateActivityOrderVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const event = previous.event
    const orderByActivityId = new Map<number, number>()
    activityOrder.forEach((activityId, index) => {
      orderByActivityId.set(activityId, index)
    })
    const sortedActivities = event.activities
      .slice()
      .sort((left, right) => left.order - right.order)
    const missingActivityIds = sortedActivities
      .map((activity) => activity.id)
      .filter((activityId) => !orderByActivityId.has(activityId))
    missingActivityIds.forEach((activityId, index) => {
      orderByActivityId.set(activityId, activityOrder.length + index)
    })
    const nextEvent: wowaudit.Event = {
      ...event,
      activities: event.activities.map((activity) => ({
        ...activity,
        order: orderByActivityId.get(activity.id) as number,
      })),
    }
    applyPatchedEvent(nextEvent)
    return previous
  }

  return {
    optimisticStatusUpdate,
    optimisticSelectionUpdate,
    optimisticActivitySelectionUpdate,
    optimisticActivityUpdate,
    optimisticActivityOrderUpdate,
    rollback,
  }
}

export default useOptimisticEventUpdate
